import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { useTranslation } from "react-i18next";
import { BsDownload, BsX } from "react-icons/bs";
import { TabListType } from "../../../api/types/commonTypes";
import { useConversation } from "../../../contexts/conversationContext";
import useCitationFromConversation from "../../../hooks/useCitationFromConversation";
import AnimatedTabs from "../../Common/AnimatedTabs";
import DocumentViewer from "../../Common/DocumentViewer";
import IconWrapper from "../../Common/IconWrapper";

const DownloadIcon = IconWrapper(BsDownload);
const CloseIcon = IconWrapper(BsX);

export const AnalysisPanel = () => {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "common" });
    const multipleTabList: TabListType[] = [
        { id: "partial", label: t("analysisPanel.documentSnippet") },
        { id: "full", label: t("analysisPanel.fullDocument") }
    ];

    const singleTabList: TabListType[] = [{ id: "full", label: t("analysisPanel.document") }];
    const { activeCitation, setIsAnalysisPanelTabActive } = useConversation();

    // Tabs
    const initialList = activeCitation?.split_source_file_path === activeCitation?.original_file_blob_path ? singleTabList : multipleTabList;
    const [activeTabList, setActiveTabList] = useState<TabListType[]>(initialList);
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [activeTab, setActiveTab] = useState(initialList[tabIndex].id);

    const handleTabSwitch = (newId: string) => {
        setActiveTab(newId);
        const newIndex = activeTabList.findIndex(t => t.id === newId);
        setTabIndex(newIndex >= 0 ? newIndex : 0);
    };

    const { filePath, fileDownloadPath, downloadFileName, fileExtension } = useCitationFromConversation({ tabIndex: tabIndex });

    const closeAnalysisPanel = () => setIsAnalysisPanelTabActive(false);

    const handleDownload = () => {
        if (fileDownloadPath) {
            const link = document.createElement("a");
            link.href = fileDownloadPath;
            link.download = downloadFileName ?? "";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    useEffect(() => {
        const tabList = activeCitation?.split_source_file_path === activeCitation?.original_file_blob_path ? singleTabList : multipleTabList;
        setActiveTabList(tabList);
        setTabIndex(0);
        setActiveTab(tabList[0].id);
    }, [activeCitation]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                boxSizing: "border-box",
                height: "100%",
                width: "100%",
                maxWidth: "100vw",
                overflow: "auto",
                position: "relative"
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    marginTop: "10px",
                    backgroundColor: theme.palette.animatedTabBackground,
                    borderTopLeftRadius: 8,
                    "& .MuiTabs-indicator": {
                        display: "none"
                    },
                    alignItems: "center"
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", flex: 1 }}>
                    <AnimatedTabs activeTab={activeTab} setActiveTab={handleTabSwitch} tabs={activeTabList} />
                </Box>
                {fileExtension !== "pdf" && (
                    <Tooltip title={t("download")} placement="top">
                        <IconButton
                            onClick={handleDownload}
                            sx={{
                                marginBlock: "5px"
                            }}
                        >
                            <DownloadIcon
                                style={{
                                    color: theme.palette.white,
                                    width: 22,
                                    height: 22
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={t("close")} placement="left">
                    <IconButton onClick={closeAnalysisPanel} sx={{ marginBlock: "5px", marginRight: "10px" }}>
                        <CloseIcon style={{ height: "28px", width: "28px" }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                    overflowY: "hidden"
                }}
            >
                <DocumentViewer filePath={filePath} fileDownloadPath={fileDownloadPath} downloadFileName={downloadFileName} fileExtension={fileExtension} />
            </Box>
        </Box>
    );
};
